import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

const HeaderChrome = ({ title, subtitle, eyebrow, onClose, onCloseLabel, showClose }) => (
    <div className="flex items-center gap-3 border-b border-ink-100 bg-white px-3 py-3 sm:px-5 sm:py-3.5">
        <div className="min-w-0 flex-1">
            {eyebrow && (
                <p className="mb-0.5 text-xs font-medium uppercase tracking-wider text-ink-400">
                    {eyebrow}
                </p>
            )}
            {title && (
                <h2 className="min-w-0 text-xl font-semibold tracking-tight text-ink-900">
                    {title}
                </h2>
            )}
            {subtitle && <p className="mt-1 truncate text-sm text-ink-500">{subtitle}</p>}
        </div>
        {showClose && (
            <button
                type="button"
                onClick={onClose}
                aria-label={onCloseLabel || "Close"}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
            >
                <i className="fas fa-times text-base" aria-hidden="true" />
            </button>
        )}
    </div>
);

const FooterChrome = ({ actions, fullPagePath, onClose, footer }) => {
    const hasContent = Boolean(actions || fullPagePath || footer);
    if (!hasContent) return null;

    return (
        <div className="flex w-full items-center justify-end gap-2 border-t border-ink-100 bg-ink-50 px-3 py-3 sm:px-6">
            {footer}
            {actions}
            {fullPagePath && (
                <Link
                    to={fullPagePath}
                    aria-label="Open full page"
                    title="Open full page"
                    onClick={onClose}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-50"
                >
                    <i className="fas fa-expand" aria-hidden="true" />
                    Open full page
                </Link>
            )}
        </div>
    );
};

/**
 * Default props for Cancel / dismiss actions in the modal footer (`actions`).
 * Spread onto `<Button>`: `{...detailModalDismissButtonProps}`.
 */
export const detailModalDismissButtonProps = Object.freeze({
    variant: "secondary",
    size: "sm",
});

/**
 * Reusable detail container for record-level views (Customer, Sale, etc.).
 *
 * Two modes:
 *   - mode="modal" (default): centered modal with backdrop. Use for
 *     in-context detail viewing from a list page.
 *   - mode="page":            inline chrome with no overlay/backdrop. Use as
 *     the body of a full-page route (e.g. /customers/:id).
 *
 * Header holds the title/subtitle and close. The footer holds record-level
 * actions (delete, save, etc.) and the auto-rendered "Open full page" link.
 *
 * For footer actions, use {@link detailModalDismissButtonProps} on Cancel-style
 * buttons so dismiss actions stay visually consistent (secondary, sm).
 */
const DetailModal = ({
    open = true,
    onClose,
    mode = "modal",
    title,
    subtitle,
    eyebrow,
    actions,
    fullPagePath,
    footer,
    children,
    size = "lg",
    closeLabel,
    /** Rendered inside the modal shell above backdrop + panel (nested dialogs). */
    topLayer,
}) => {
    // Lock body scroll while the modal is open.
    useEffect(() => {
        if (mode !== "modal" || !open) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = original;
        };
    }, [mode, open]);

    // Close on Escape (modal mode only).
    useEffect(() => {
        if (mode !== "modal" || !open || !onClose) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [mode, open, onClose]);

    if (mode === "page") {
        return (
            <div className="overflow-hidden rounded-2xl border border-ink-100 bg-white shadow-card">
                <HeaderChrome
                    title={title}
                    subtitle={subtitle}
                    eyebrow={eyebrow}
                    showClose={false}
                    onClose={onClose}
                    onCloseLabel={closeLabel}
                />
                <div className="px-3 py-4 sm:px-6 sm:py-6">{children}</div>
                <FooterChrome
                    actions={actions}
                    fullPagePath={null}
                    onClose={onClose}
                    footer={footer}
                />
            </div>
        );
    }

    if (!open) return null;

    const widthClass =
        {
            md: "sm:max-w-lg",
            lg: "sm:max-w-3xl",
            xl: "sm:max-w-5xl",
            full: "sm:max-w-7xl",
        }[size] || "sm:max-w-3xl";

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === "string" ? title : undefined}
            className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6"
        >
            {/* Backdrop — dark overlay with subtle blur for clear modal contrast */}
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 bg-ink-900 bg-opacity-60 backdrop-blur-sm transition-opacity"
            />

            {/* Modal panel */}
            <div
                className={`relative flex max-h-modal-sm w-full flex-col overflow-hidden rounded-xl border border-ink-200 bg-white shadow-2xl sm:max-h-modal sm:rounded-2xl ${widthClass}`}
            >
                <HeaderChrome
                    title={title}
                    subtitle={subtitle}
                    eyebrow={eyebrow}
                    showClose
                    onClose={onClose}
                    onCloseLabel={closeLabel}
                />

                <div className="flex-1 overflow-y-auto bg-white px-3 py-3 sm:px-6 sm:py-5">
                    {children}
                </div>

                <FooterChrome
                    actions={actions}
                    fullPagePath={fullPagePath}
                    onClose={onClose}
                    footer={footer}
                />
            </div>

            {topLayer}
        </div>,
        document.body,
    );
};

DetailModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    mode: PropTypes.oneOf(["modal", "page"]),
    title: PropTypes.node,
    subtitle: PropTypes.node,
    eyebrow: PropTypes.node,
    actions: PropTypes.node,
    fullPagePath: PropTypes.string,
    footer: PropTypes.node,
    children: PropTypes.node,
    size: PropTypes.oneOf(["md", "lg", "xl", "full"]),
    closeLabel: PropTypes.string,
    topLayer: PropTypes.node,
};

export default DetailModal;
